import React, { useEffect, useMemo, useState } from "react";
import { api, fmtErr, kg, num, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import DateDMY from "@/components/DateDMY";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ClipboardCheck, ImageIcon, FileText, CheckCircle2, AlertTriangle, Files } from "lucide-react";

const REJECTION_REASONS = [
  "Wet Bag/Carton",
  "Torn Bag/Cover",
  "Short Count",
  "Leakage",
  "Others",
];

const OVERDUE_DAYS = 3;
const MAX_FILE_MB = 4;

const podBadge = (p) => ({
  Pending: "bg-slate-100 text-slate-700 border-slate-300",
  Received: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Damaged: "bg-rose-50 text-rose-700 border-rose-200",
  Missing: "bg-amber-50 text-amber-700 border-amber-200",
  "Not Required": "bg-slate-100 text-slate-500 border-slate-300 italic",
}[p] || "bg-slate-100 text-slate-700");

const isPdfDataUrl = (u) => typeof u === "string" && u.startsWith("data:application/pdf");
const isImageDataUrl = (u) => typeof u === "string" && u.startsWith("data:image");

function daysSince(dateStr) {
  if (!dateStr) return 0;
  const [y, m, d] = dateStr.split("-").map((x) => parseInt(x, 10));
  const dispatch = new Date(y, (m || 1) - 1, d || 1);
  const now = new Date();
  return Math.floor((now - dispatch) / (1000 * 60 * 60 * 24));
}

async function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function DocPreview({ url, testid }) {
  if (!url) return <span className="text-slate-400 text-xs">—</span>;
  if (isPdfDataUrl(url)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" download="pod.pdf" className="text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={testid}>
        <FileText className="h-4 w-4" /> PDF
      </a>
    );
  }
  return <img src={url} alt="POD" className="h-10 w-14 object-cover rounded border" data-testid={testid} />;
}

export default function PODReconcile() {
  const { can } = useAuth();
  const canEdit = can("pod", "edit");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [fromD, setFromD] = useState("");
  const [toD, setToD] = useState("");
  const [statusF, setStatusF] = useState("Pending");
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Single reconcile modal state
  const [reconciling, setReconciling] = useState(null);
  const [f, setF] = useState({ pod: "Received", delivery_date: "", damaged_missing_qty: "", note: "", photo_base64: "", rejection_reason: "" });
  const [photoPreview, setPhotoPreview] = useState("");
  const [viewing, setViewing] = useState(null);

  // Bulk selection state
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ pod: "Received", delivery_date: new Date().toISOString().slice(0, 10) });
  const [bulkFiles, setBulkFiles] = useState({}); // {dispatch_id: {file_data_url, damaged_missing_qty}}

  const load = async () => setRows((await api.get("/dispatches")).data);
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (fromD && r.date < fromD) return false;
      if (toD && r.date > toD) return false;
      if (statusF !== "all" && r.pod !== statusF) return false;
      if (overdueOnly) {
        if (r.pod !== "Pending") return false;
        if (daysSince(r.date) < OVERDUE_DAYS) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        return [r.vehicle_number, r.customer_name, r.invoice_number, r.lr_gr_number].some((x) => (x || "").toLowerCase().includes(q));
      }
      return true;
    });
  }, [rows, search, fromD, toD, statusF, overdueOnly]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selectedRows = filtered.filter((r) => selected[r.id]);
  const allPageSelected = filtered.length > 0 && filtered.every((r) => selected[r.id]);

  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const next = {};
    if (!allPageSelected) filtered.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };

  // -- Single reconcile handlers --
  const openReconcile = (row) => {
    setReconciling(row);
    setF({
      pod: row.pod && row.pod !== "Pending" ? row.pod : "Received",
      delivery_date: row.delivery_date || new Date().toISOString().slice(0, 10),
      damaged_missing_qty: row.damaged_missing_qty || "",
      note: row.pod_note || "",
      photo_base64: "",
      rejection_reason: row.rejection_reason || "",
    });
    setPhotoPreview(row.pod_photo || "");
  };

  const onFile = async (e, setter) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`File must be under ${MAX_FILE_MB} MB`); return; }
    const isImg = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImg && !isPdf) { toast.error("Only image or PDF files are accepted"); return; }
    const dataUrl = await readFileAsDataUrl(file);
    setter(dataUrl);
  };

  const submit = async () => {
    if (!f.delivery_date) return toast.error("Delivery Date is required");
    const hasPhoto = f.photo_base64 || (reconciling && reconciling.pod_photo);
    if (!hasPhoto) return toast.error("POD document/image is required");
    if (f.pod !== "Received") {
      const dmq = parseFloat(f.damaged_missing_qty || 0);
      if (!(dmq > 0)) return toast.error(`Enter ${f.pod} quantity`);
      if (dmq > reconciling.quantity) return toast.error("Cannot exceed dispatched quantity");
      if (!f.rejection_reason) return toast.error("Select a reason");
    }
    try {
      await api.post(`/dispatches/${reconciling.id}/pod-reconcile`, {
        pod: f.pod,
        delivery_date: f.delivery_date,
        damaged_missing_qty: f.pod === "Received" ? 0 : parseFloat(f.damaged_missing_qty || 0),
        note: f.note,
        photo_base64: f.photo_base64,
        rejection_reason: f.pod === "Received" ? "" : f.rejection_reason,
      });
      toast.success("POD reconciled. Change logged to audit trail.");
      setReconciling(null); setPhotoPreview(""); load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  // -- Bulk handlers --
  const openBulk = () => {
    if (selectedRows.length === 0) return;
    const init = {};
    selectedRows.forEach((r) => { init[r.id] = { file_data_url: "", damaged_missing_qty: "", rejection_reason: "" }; });
    setBulkFiles(init);
    setBulk({ pod: "Received", delivery_date: new Date().toISOString().slice(0, 10) });
    setBulkOpen(true);
  };

  const onBulkFile = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`File must be under ${MAX_FILE_MB} MB`); return; }
    const dataUrl = await readFileAsDataUrl(file);
    setBulkFiles((s) => ({ ...s, [id]: { ...s[id], file_data_url: dataUrl } }));
  };

  const submitBulk = async () => {
    if (!bulk.delivery_date) return toast.error("Delivery Date is required");
    for (const r of selectedRows) {
      const b = bulkFiles[r.id] || {};
      if (!b.file_data_url) return toast.error(`Attach POD doc for ${r.invoice_number}`);
      if (bulk.pod !== "Received") {
        const dmq = parseFloat(b.damaged_missing_qty || 0);
        if (!(dmq > 0)) return toast.error(`Enter ${bulk.pod} qty for ${r.invoice_number}`);
        if (dmq > r.quantity) return toast.error(`${bulk.pod} qty exceeds dispatched qty for ${r.invoice_number}`);
        if (!b.rejection_reason) return toast.error(`Select reason for ${r.invoice_number}`);
      }
    }
    try {
      await api.post("/dispatches/pod-reconcile-batch", {
        pod: bulk.pod,
        delivery_date: bulk.delivery_date,
        items: selectedRows.map((r) => ({
          dispatch_id: r.id,
          photo_base64: bulkFiles[r.id].file_data_url,
          damaged_missing_qty: bulk.pod === "Received" ? 0 : parseFloat(bulkFiles[r.id].damaged_missing_qty || 0),
          note: "",
          rejection_reason: bulk.pod === "Received" ? "" : (bulkFiles[r.id].rejection_reason || ""),
        })),
      });
      toast.success(`${selectedRows.length} PODs reconciled`);
      setBulkOpen(false); setSelected({}); load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  const pendingCount = rows.filter((r) => r.pod === "Pending").length;
  const overdueCount = rows.filter((r) => r.pod === "Pending" && daysSince(r.date) >= OVERDUE_DAYS).length;

  return (
    <div className="space-y-6" data-testid="pod-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardCheck className="h-7 w-7 text-blue-600" /> POD Reconciliation
          </h1>
          <p className="text-slate-500 text-sm mt-1">Capture POD status, delivery date and a POD document (image or PDF) for each dispatch.</p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-sm">
              <b className="font-mono-num">{pendingCount}</b> pending
            </div>
          )}
          {overdueCount > 0 && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="overdue-header-badge">
              <AlertTriangle className="h-3.5 w-3.5" />
              <b className="font-mono-num">{overdueCount}</b> overdue ({OVERDUE_DAYS}+ days)
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3 flex-wrap items-end">
        <div className="flex-1 min-w-[220px]"><Label>Search</Label><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Vehicle, customer, invoice…" data-testid="pod-search" /></div>
        <div><Label>Dispatch From</Label><DateDMY value={fromD} onChange={setFromD} testid="pod-from" /></div>
        <div><Label>Dispatch To</Label><DateDMY value={toD} onChange={setToD} minDate={fromD} testid="pod-to" /></div>
        <div className="min-w-[160px]">
          <Label>POD Status</Label>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger data-testid="pod-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Received">Received</SelectItem>
              <SelectItem value="Damaged">Damaged</SelectItem>
              <SelectItem value="Missing">Missing</SelectItem>
              <SelectItem value="Not Required">Not Required</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer h-10 px-3 rounded-md border bg-white">
          <Switch checked={overdueOnly} onCheckedChange={setOverdueOnly} data-testid="overdue-toggle" />
          <span>Overdue only</span>
        </label>
      </div>

      {selectedIds.length > 0 && canEdit && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
          <div className="text-sm text-blue-900"><b>{selectedIds.length}</b> row{selectedIds.length === 1 ? "" : "s"} selected</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected({})} data-testid="clear-selection-btn">Clear</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={openBulk} data-testid="bulk-reconcile-btn">
              <Files className="h-3.5 w-3.5 mr-1.5" /> Reconcile Selected
            </Button>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-xs uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2.5">
                {canEdit && <Checkbox checked={allPageSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />}
              </th>
              {["Dispatch Date", "Age", "Vehicle", "Transporter", "Customer", "Invoice", "Product", "Qty", "Weight", "POD", "Delivery Date", "D/M Qty", "POD Doc", "Reconciled", "Action"].map((h) =>
                <th key={h} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const age = daysSince(r.date);
              const overdue = r.pod === "Pending" && age >= OVERDUE_DAYS;
              return (
                <tr key={r.id} className={`border-b hover:bg-slate-50 ${overdue ? "bg-rose-50/40" : ""}`} data-testid={`pod-row-${i}`}>
                  <td className="px-3 py-2">
                    {canEdit && r.pod === "Pending" && (
                      <Checkbox checked={!!selected[r.id]} onCheckedChange={() => toggleSelect(r.id)} data-testid={`select-row-${i}`} />
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono-num whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2 font-mono-num text-xs">
                    {r.pod === "Pending" ? (
                      <span className={overdue ? "text-rose-700 font-semibold" : "text-slate-600"}>{age}d</span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 font-medium">{r.vehicle_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap" data-testid={`pod-transporter-${i}`}>
                    {r.vehicle_type === "customer" ? <span className="text-slate-400 italic">— Not Applicable</span> : (r.transporter_name || "—")}
                  </td>
                  <td className="px-3 py-2">{r.customer_name}</td>
                  <td className="px-3 py-2 font-mono-num">{r.invoice_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.product}{r.variant ? ` · ${r.variant}` : ""}</td>
                  <td className="px-3 py-2 font-mono-num text-right">{num(r.quantity, 0)}</td>
                  <td className="px-3 py-2 font-mono-num text-right">{kg(r.weight_kg)}</td>
                  <td className="px-3 py-2">
                    <span className={`pill border ${podBadge(r.pod)}`}>{r.pod}</span>
                    {overdue && <span className="ml-1 text-[10px] text-rose-700 font-semibold uppercase">Overdue</span>}
                  </td>
                  <td className="px-3 py-2 font-mono-num whitespace-nowrap">{fmtDate(r.delivery_date) || "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-right">{r.pod === "Damaged" || r.pod === "Missing" ? num(r.damaged_missing_qty, 2) : "-"}</td>
                  <td className="px-3 py-2">
                    {r.pod_photo ? (
                      isPdfDataUrl(r.pod_photo) ? (
                        <a href={r.pod_photo} target="_blank" rel="noreferrer" download="pod.pdf" className="text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={`pod-view-photo-${i}`}>
                          <FileText className="h-4 w-4" /> PDF
                        </a>
                      ) : (
                        <button onClick={() => setViewing(r.pod_photo)} data-testid={`pod-view-photo-${i}`} className="text-blue-600 hover:underline inline-flex items-center gap-1">
                          <ImageIcon className="h-4 w-4" /> View
                        </button>
                      )
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                    {r.pod_reconciled_at ? (
                      <div>
                        <div className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> {r.pod_reconciled_by_name}</div>
                        <div className="text-slate-500">{new Date(r.pod_reconciled_at).toLocaleString()}</div>
                      </div>
                    ) : <span className="text-slate-400">Not yet</span>}
                  </td>
                  <td className="px-3 py-2">
                    {canEdit && r.pod !== "Not Required" && (
                      <Button size="sm" variant="outline" onClick={() => openReconcile(r)} data-testid={`pod-reconcile-btn-${i}`}>
                        {r.pod === "Pending" ? "Enter POD" : "Update POD"}
                      </Button>
                    )}
                    {r.pod === "Not Required" && <span className="text-xs text-slate-400 italic">Not applicable</span>}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="16" className="text-center py-8 text-slate-500">No dispatches match.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Single reconcile modal */}
      <Dialog open={!!reconciling} onOpenChange={(o) => !o && setReconciling(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Reconcile POD</DialogTitle></DialogHeader>
          {reconciling && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded-md">
                <div><b>{reconciling.vehicle_number}</b> · {reconciling.customer_name}</div>
                <div className="text-xs mt-0.5">Invoice {reconciling.invoice_number} · Dispatch {fmtDate(reconciling.date)} · Qty {reconciling.quantity} · {reconciling.product} {reconciling.variant}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>POD Status *</Label>
                  <Select value={f.pod} onValueChange={(v) => setF({ ...f, pod: v })}>
                    <SelectTrigger data-testid="pod-modal-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Received">Received</SelectItem>
                      <SelectItem value="Damaged">Damaged</SelectItem>
                      <SelectItem value="Missing">Missing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Delivery Date *</Label>
                  <Input type="hidden" value={f.delivery_date} readOnly />
                  <DateDMY value={f.delivery_date}
                    onChange={(iso) => setF({ ...f, delivery_date: iso })}
                    testid="pod-modal-delivery-date" />
                </div>
              </div>
              {f.pod !== "Received" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{f.pod} Quantity * <span className="text-xs text-slate-500">(max {reconciling.quantity})</span></Label>
                    <Input type="number" min="0" step="0.01" value={f.damaged_missing_qty}
                      onChange={(e) => setF({ ...f, damaged_missing_qty: e.target.value })}
                      data-testid="pod-modal-qty-input" />
                  </div>
                  <div>
                    <Label>Reason *</Label>
                    <Select value={f.rejection_reason} onValueChange={(v) => setF({ ...f, rejection_reason: v })}>
                      <SelectTrigger data-testid="pod-modal-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                      <SelectContent>
                        {REJECTION_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div>
                <Label>POD Document / Image or PDF * <span className="text-xs text-slate-500">(≤{MAX_FILE_MB} MB)</span></Label>
                <Input type="file" accept="image/*,application/pdf" onChange={(e) => onFile(e, (u) => { setF((s) => ({ ...s, photo_base64: u })); setPhotoPreview(u); })} data-testid="pod-modal-photo-input" />
                {photoPreview ? (
                  isPdfDataUrl(photoPreview) ? (
                    <a href={photoPreview} target="_blank" rel="noreferrer" download="pod.pdf" className="mt-2 inline-flex items-center gap-1.5 text-blue-600 hover:underline">
                      <FileText className="h-4 w-4" /> Preview PDF
                    </a>
                  ) : (
                    <img src={photoPreview} alt="POD" className="mt-2 max-h-40 rounded-md border" />
                  )
                ) : (
                  <p className="text-xs text-rose-600 mt-1">POD upload is mandatory for every status.</p>
                )}
              </div>
              <div>
                <Label>Note</Label>
                <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })}
                  placeholder="Optional details…" data-testid="pod-modal-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconciling(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submit} data-testid="pod-modal-save-btn">Save POD</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image viewer */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>POD Document</DialogTitle></DialogHeader>
          {viewing && (isImageDataUrl(viewing) ? <img src={viewing} alt="POD" className="w-full rounded-md" /> : <p className="text-sm">Not an image.</p>)}
        </DialogContent>
      </Dialog>

      {/* Bulk reconcile modal */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Files className="h-4 w-4" /> Bulk POD Reconciliation ({selectedRows.length})</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>POD Status (applied to all) *</Label>
                <Select value={bulk.pod} onValueChange={(v) => setBulk({ ...bulk, pod: v })}>
                  <SelectTrigger data-testid="bulk-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Received">Received</SelectItem>
                    <SelectItem value="Damaged">Damaged</SelectItem>
                    <SelectItem value="Missing">Missing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Delivery Date (applied to all) *</Label>
                <DateDMY value={bulk.delivery_date}
                  onChange={(iso) => setBulk({ ...bulk, delivery_date: iso })}
                  testid="bulk-delivery-date" />
              </div>
            </div>

            <div className="max-h-[420px] overflow-y-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 text-slate-600 uppercase">
                  <tr>
                    <th className="px-2 py-2 text-left">Invoice</th>
                    <th className="px-2 py-2 text-left">Customer</th>
                    <th className="px-2 py-2 text-left">Product · Qty</th>
                    {bulk.pod !== "Received" && <th className="px-2 py-2 text-left">{bulk.pod} Qty *</th>}
                    {bulk.pod !== "Received" && <th className="px-2 py-2 text-left">Reason *</th>}
                    <th className="px-2 py-2 text-left">POD Doc * <span className="normal-case text-[10px] text-slate-500">(img/PDF ≤{MAX_FILE_MB}MB)</span></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRows.map((r, i) => {
                    const b = bulkFiles[r.id] || {};
                    return (
                      <tr key={r.id} className="border-t" data-testid={`bulk-row-${i}`}>
                        <td className="px-2 py-2 font-mono-num">{r.invoice_number}</td>
                        <td className="px-2 py-2">{r.customer_name}</td>
                        <td className="px-2 py-2 whitespace-nowrap">{r.product}{r.variant ? ` · ${r.variant}` : ""} · <b>{r.quantity}</b></td>
                        {bulk.pod !== "Received" && (
                          <td className="px-2 py-2">
                            <Input type="number" min="0" max={r.quantity} step="0.01" className="h-8"
                              value={b.damaged_missing_qty || ""}
                              onChange={(e) => setBulkFiles((s) => ({ ...s, [r.id]: { ...s[r.id], damaged_missing_qty: e.target.value } }))}
                              data-testid={`bulk-qty-${i}`} />
                          </td>
                        )}
                        {bulk.pod !== "Received" && (
                          <td className="px-2 py-2 min-w-[140px]">
                            <Select value={b.rejection_reason || ""} onValueChange={(v) => setBulkFiles((s) => ({ ...s, [r.id]: { ...s[r.id], rejection_reason: v } }))}>
                              <SelectTrigger className="h-8" data-testid={`bulk-reason-${i}`}><SelectValue placeholder="Reason" /></SelectTrigger>
                              <SelectContent>
                                {REJECTION_REASONS.map((rr) => <SelectItem key={rr} value={rr}>{rr}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                        )}
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-2">
                            <Input type="file" accept="image/*,application/pdf" className="h-8 py-0"
                              onChange={(e) => onBulkFile(r.id, e)}
                              data-testid={`bulk-file-${i}`} />
                            {b.file_data_url && <DocPreview url={b.file_data_url} testid={`bulk-preview-${i}`} />}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitBulk} data-testid="bulk-submit-btn">
              Reconcile {selectedRows.length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
